import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Container, Typography, Grid, Card, CardContent, Chip,
  LinearProgress, Button, Avatar, IconButton, Breadcrumbs, Stack,
  Link as MuiLink, Fade, Skeleton, Alert, Paper, TextField, InputAdornment
} from '@mui/material';
import {
  NavigateNext as NavigateNextIcon,
  PlayArrow as PlayIcon,
  CheckCircle as CompletedIcon,
  Lock as LockIcon,
  TrendingUp as TrendingUpIcon,
  School as SchoolIcon,
  Timeline as TimelineIcon,
  EmojiEvents as TrophyIcon,
  ArrowBack as ArrowBackIcon,
  Home as HomeIcon
} from '@mui/icons-material';
import SearchIcon from '@mui/icons-material/Search';
import { useAuth } from '../context/AuthContext';
import { getSyllabusWithProgress, getSubjectWithProgress } from '../services/subjectPracticeService';
import ErrorBoundary from '../components/ErrorBoundary';
import TopicGrid from '../components/subject-practice/TopicGrid';
import useProgressQueue from '../hooks/useProgressQueue';
import { CircularProgress } from '@mui/material';

const UI_TOKENS = {
  heroGradient: 'radial-gradient(circle at 70% 30%, rgba(10, 126, 164, 0.08) 0%, rgba(255, 255, 255, 0) 52%), linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)',
  chipBg: 'rgba(10, 126, 164, 0.10)',
  accent: '#0a7ea4',
  radius: '16px',
};


const SubjectPractice = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const [subjects, setSubjects] = useState([]);
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [selectedView, setSelectedView] = useState('overview'); // 'overview', 'subject', 'topic'
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [readMoreSubject, setReadMoreSubject] = useState(null); // Subject object for popup
  const { enqueue, queue } = useProgressQueue();
  const [syncingItems, setSyncingItems] = useState({}); // conceptId -> queueItemId

  // Stats for the dashboard
  const [stats, setStats] = useState({
    totalStudyTime: 245, // minutes
    achievementsEarned: 12,
    averageScore: 87,
    currentStreak: 7,
    totalSubjects: 4,
    completedSubjects: 1,
    totalTopics: 84,
    completedTopics: 47
  });

  useEffect(() => {
    loadSubjects();
  }, []);

  useEffect(() => {
    if (!Array.isArray(subjects) || subjects.length === 0) return;
    const totalSubjects = subjects.length;
    const completedSubjects = subjects.filter((s) => Number(s.progress || 0) >= 100).length;
    const totalTopics = subjects.reduce((acc, s) => acc + Number(s.totalTopics || 0), 0);
    const completedTopics = subjects.reduce((acc, s) => acc + Number(s.completedTopics || 0), 0);
    setStats((prev) => ({
      ...prev,
      totalSubjects,
      completedSubjects,
      totalTopics,
      completedTopics,
    }));
  }, [subjects]);

  const loadSubjects = async () => {
    try {
      setLoading(true);
      setError('');

      let syllabusData = null;

      // Try to get syllabus from API
      try {
        syllabusData = await getSyllabusWithProgress(user?.id);
        console.log('Syllabus API Response:', syllabusData);
      } catch (apiErr) {
        console.warn('API not available:', apiErr?.message || apiErr);
      }

      // Check if we have valid data from API. Accept shapes: Array, { data: [] }, or { subjects: [] }
      const hasSubjects = (
        Array.isArray(syllabusData) && syllabusData.length > 0
      ) || (
        syllabusData && Array.isArray(syllabusData.data) && syllabusData.data.length > 0
      ) || (
        syllabusData && Array.isArray(syllabusData.subjects) && syllabusData.subjects.length > 0
      );

      // If API provided no subjects, show a short error and do not fallback to the huge local syllabus
      if (!syllabusData || !hasSubjects) {
        console.warn('No syllabus returned from API');
        setSubjects([]);
        setError('No Syllabus Loaded. Please try after some time');
        return;
      }

      // Accept multiple API shapes: array, { data: [] }, or { subjects: [] }
      let subjectsArray = [];
      if (Array.isArray(syllabusData)) subjectsArray = syllabusData;
      else if (Array.isArray(syllabusData.data)) subjectsArray = syllabusData.data;
      else if (Array.isArray(syllabusData.subjects)) subjectsArray = syllabusData.subjects;

      // Process subjects from API response or local data
      const processedSubjects = subjectsArray?.map(subject => {
        const totalTopics = subject.topics?.length || 0;
        const totalConcepts = calculateTotalConcepts(subject);
        // Since API doesn't return progress initially, default to 0
        const completedTopics = 0;
        const progress = 0;

        return {
          ...subject,
          totalTopics,
          totalConcepts,
          completedTopics,
          progress,
          // Use colors and icons from local data if available, otherwise fallback
          color: subject.color || getSubjectColor(subject.id),
          icon: subject.icon || getSubjectIcon(subject.id),
          estimatedHours: subject.estimatedHours || totalTopics * 5,
          difficulty: subject.difficulty || 'intermediate'
        };
      }) || [];

      console.log('Processed subjects:', processedSubjects);
      setSubjects(processedSubjects);
    } catch (err) {
      console.error('Failed to load subjects:', err);
      console.error('Error details:', err.message, err.response);

      // Show a friendly message when load fails
      setSubjects([]);
      setError('No Syllabus Loaded. Please try after some time');
    } finally {
      setLoading(false);
    }
  };

  // Helper function to calculate total concepts in a subject
  const calculateTotalConcepts = (subject) => {
    let total = 0;
    subject.topics?.forEach(topic => {
      topic.subtopics?.forEach(subtopic => {
        total += subtopic.concepts?.length || 0;
      });
    });
    return total;
  };

  const filteredSubjects = subjects.filter(s => {
    if (!search) return true;
    return (s.name || '').toLowerCase().includes(search.toLowerCase()) || (s.description || '').toLowerCase().includes(search.toLowerCase());
  });

  const getSubjectColor = (subjectId) => {
    const colors = {
      mathematics: '#2196F3',
      english: '#4CAF50',
      hindi: '#FF9800',
      computer: '#9C27B0',
      reasoning: '#F44336',
      general_studies_indian: '#00BCD4',
      indian_history: '#795548',
      indian_geography: '#8BC34A'
    };
    return colors[subjectId] || '#2196F3';
  };

  const getSubjectIcon = (subjectId) => {
    const icons = {
      mathematics: 'calculate',
      english: 'book',
      hindi: 'book',
      computer: 'computer',
      reasoning: 'psychology',
      general_studies_indian: 'public',
      indian_history: 'history_edu',
      indian_geography: 'public'
    };
    return icons[subjectId] || 'school';
  };

  const handleSubjectClick = async (subject) => {
    try {
      setLoading(true);

      // Use the subject object we already received from the syllabus API.
      // Avoid an additional per-subject API call since topics/subtopics are already present.
      let subjectData = subject;

      // Unwrap API response shapes: { data: subject } or direct subject
      if (subjectData && subjectData.data) subjectData = subjectData.data;

      // If API failed and local data not found, use passed subject
      if (!subjectData) {
        subjectData = subject;
      }

      // Process topics with progress calculation
      const processedTopics = subjectData.topics?.map((topic, index) => {
        const totalSubtopics = topic.subtopics?.length || 0;
        const totalConcepts = topic.subtopics?.reduce((sum, st) => sum + (st.concepts?.length || 0), 0) || 0;
        
        // Default progress to 0 since API doesn't return it initially
        const completedSubtopics = 0;
        const completedConcepts = 0;
        const progress = 0;

        // Determine topic status - first topic is in progress, others are locked
        let completed = false;
        let inProgress = index === 0; // Only first topic is in progress
        let locked = index > 0; // All other topics are locked

        // Process subtopics with their concepts
        const processedSubtopics = (topic.subtopics || []).map((subtopic, stIndex) => {
          const concepts = subtopic.concepts?.map((concept, cIndex) => ({
            ...concept,
            completed: false,
            inProgress: index === 0 && stIndex === 0 && cIndex === 0,
            locked: index > 0 || stIndex > 0 || cIndex > 0
          })) || [];

          return {
            ...subtopic,
            concepts,
            totalConcepts: concepts.length,
            completedConcepts: 0,
            progress: 0,
            completed: false,
            inProgress: index === 0 && stIndex === 0, // Only first subtopic of first topic is in progress
            locked: index > 0 || (index === 0 && stIndex > 0)
          };
        }) || [];

        return {
          ...topic,
          progress,
          completed,
          inProgress,
          locked,
          totalSubtopics,
          totalConcepts,
          completedSubtopics,
          completedConcepts,
          subtopics: processedSubtopics
        };
      }) || [];

      const subjectWithTopics = {
        ...subject,
        ...subjectData,
        topics: processedTopics
      };

      setSelectedSubject(subjectWithTopics);
      setSelectedView('subject');
    } catch (err) {
      console.error('Failed to load subject details:', err);
      // Fallback to basic subject data
      setSelectedSubject(subject);
      setSelectedView('subject');
    } finally {
      setLoading(false);
    }
  };

  const handleTopicClick = (subject, topic) => {
    // Topic should already have subtopics loaded from the subject detail API call
    setSelectedTopic(topic);
    setSelectedView('topic');
  };

  // Watch queue and clear syncing items when their queue entry is processed
  useEffect(() => {
    const queueIds = (queue || []).map(i => i.id);
    setSyncingItems(prev => {
      const next = { ...prev };
      Object.entries(prev).forEach(([conceptId, qid]) => {
        if (!queueIds.includes(qid)) delete next[conceptId];
      });
      return next;
    });
  }, [queue]);

  const markConceptCompleted = (concept) => {
    if (!user?.id || !concept) return;

    const clientToken = `${Date.now()}-${Math.random().toString(36).slice(2,9)}`;

    // Optimistic local update: mark concept completed in subjects state and selectedTopic
    setSubjects(prev => prev.map(subj => {
      // update across all subjects
      const newSubj = { ...subj };
      newSubj.topics = (newSubj.topics || []).map(topic => {
        const newTopic = { ...topic };
        newTopic.subtopics = (newTopic.subtopics || []).map(st => {
          const newSt = { ...st };
          newSt.concepts = (newSt.concepts || []).map(c => {
            if (c.id === concept.id) {
              return { ...c, completed: true, inProgress: false };
            }
            return c;
          });
          return newSt;
        });
        return newTopic;
      });
      return newSubj;
    }));

    // Also update selectedTopic/subtopic if present
    setSelectedTopic(prevTopic => {
      if (!prevTopic) return prevTopic;
      const newTopic = { ...prevTopic };
      newTopic.subtopics = (newTopic.subtopics || []).map(st => ({
        ...st,
        concepts: (st.concepts || []).map(c => c.id === concept.id ? { ...c, completed: true, inProgress: false } : c)
      }));
      return newTopic;
    });

    // Enqueue background sync
    const item = enqueue({
      type: 'concept',
      userId: user.id,
      targetId: concept.id,
      payload: { clientToken, status: 'completed', score: concept.score || 0, timeSpent: concept.duration || 0 }
    });

    // Track syncing state
    setSyncingItems(prev => ({ ...prev, [concept.id]: item.id }));
  };

  const handleBackToOverview = () => {
    setSelectedView('overview');
    setSelectedSubject(null);
    setSelectedTopic(null);
  };

  const handleBackToSubject = () => {
    setSelectedView('subject');
    setSelectedTopic(null);
  };

  const handleStartLearning = (subject) => {
    // Navigate to the first available topic/concept
    navigate(`/subject-practice/${subject.id}/learn`);
  };

  const renderOverview = () => (
    <Fade in={selectedView === 'overview'} timeout={500}>
      <Box>
        <Box 
          sx={{ 
            background: UI_TOKENS.heroGradient,
            color: 'text.primary',
            py: 8,
            borderBottom: '1px solid',
            borderColor: 'divider'
          }}
        >
          <Container maxWidth="xl">
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={7}>
                <Typography 
                  variant="overline" 
                  sx={{ 
                    color: UI_TOKENS.accent, 
                    fontWeight: 800, 
                    letterSpacing: 2,
                    mb: 1.5,
                    display: 'block'
                  }}
                >
                  Academic Excellence
                </Typography>
                <Typography variant="h3" fontWeight={900} sx={{ mb: 2, letterSpacing: '-0.02em', color: 'primary.dark' }}>
                  Smart Practice. <Box component="span" sx={{ color: UI_TOKENS.accent }}>Master Topics.</Box>
                </Typography>
                <Typography variant="h6" sx={{ mb: 4, opacity: 0.8, fontWeight: 500, maxWidth: 600 }}>
                  Personalized practice paths powered by AI to help you achieve your goal. Select a subject to begin your mastery journey.
                </Typography>
                <Box display="flex" gap={2}>
                  <Chip 
                    icon={<SchoolIcon sx={{ color: `${UI_TOKENS.accent} !important` }} />}
                    label="NCERT Aligned" 
                    sx={{ bgcolor: UI_TOKENS.chipBg, color: UI_TOKENS.accent, fontWeight: 700, borderRadius: UI_TOKENS.radius }}
                  />
                  <Chip 
                    icon={<TimelineIcon sx={{ color: `${UI_TOKENS.accent} !important` }} />}
                    label="Growth Tracking" 
                    sx={{ bgcolor: UI_TOKENS.chipBg, color: UI_TOKENS.accent, fontWeight: 700, borderRadius: UI_TOKENS.radius }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={5}>
                <TextField
                  fullWidth
                  placeholder="Search subjects, topics or concepts..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: UI_TOKENS.accent }} />
                      </InputAdornment>
                    ),
                    sx: {
                      bgcolor: 'white',
                      borderRadius: UI_TOKENS.radius,
                      height: 64,
                      fontSize: '1.1rem',
                      boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                      '& fieldset': { border: 'none' }
                    }
                  }}
                />
              </Grid>
            </Grid>
          </Container>
        </Box>

        <Grid container spacing={2} sx={{ mb: 5 }}>
          <Grid item xs={6} sm={3}><Paper sx={{ p: 3, borderRadius: UI_TOKENS.radius, textAlign: 'center', border: '1px solid', borderColor: 'divider' }} elevation={0}><Typography variant="h4" fontWeight={900} color="primary">{stats.totalSubjects}</Typography><Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em' }}>Subjects</Typography></Paper></Grid>
          <Grid item xs={6} sm={3}><Paper sx={{ p: 3, borderRadius: UI_TOKENS.radius, textAlign: 'center', border: '1px solid', borderColor: 'divider' }} elevation={0}><Typography variant="h4" fontWeight={900} color="success.main">{stats.completedSubjects}</Typography><Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em' }}>Completed</Typography></Paper></Grid>
          <Grid item xs={6} sm={3}><Paper sx={{ p: 3, borderRadius: UI_TOKENS.radius, textAlign: 'center', border: '1px solid', borderColor: 'divider' }} elevation={0}><Typography variant="h4" fontWeight={900} color="info.main">{stats.totalTopics}</Typography><Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em' }}>Topics</Typography></Paper></Grid>
          <Grid item xs={6} sm={3}><Paper sx={{ p: 3, borderRadius: UI_TOKENS.radius, textAlign: 'center', border: '1px solid', borderColor: 'divider' }} elevation={0}><Typography variant="h4" fontWeight={900} color="warning.main">{stats.currentStreak}</Typography><Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em' }}>Streak</Typography></Paper></Grid>
        </Grid>

        {loading ? (
          <Grid container spacing={4}>
            {[...Array(4)].map((_, i) => (
              <Grid item xs={12} sm={6} md={6} lg={4} xl={3} key={i}>
                <Card sx={{ borderRadius: UI_TOKENS.radius, height: 'auto', minHeight: 280, border: '1px solid', borderColor: 'divider' }}>
                  <CardContent sx={{ p: 3 }}>
                    <Skeleton variant="circular" width={64} height={64} sx={{ mb: 3, mx: 'auto' }} />
                    <Skeleton variant="text" height={32} width="80%" sx={{ mx: 'auto', mb: 2 }} />
                    <Skeleton variant="text" height={20} width="100%" sx={{ mb: 1 }} />
                    <Skeleton variant="text" height={20} width="60%" sx={{ mx: 'auto' }} />
                    <Skeleton variant="rectangular" height={8} width="100%" sx={{ mt: 3, borderRadius: UI_TOKENS.radius }} />
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Box 
            display="grid" 
            gridTemplateColumns={{ 
              xs: '1fr', 
              sm: 'repeat(2, 1fr)', 
              md: 'repeat(3, 1fr)' 
            }} 
            gap={4}
          >
            {filteredSubjects.map((subject) => (
              <Box key={subject.id} sx={{ minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                <Card
                  sx={{
                    borderRadius: UI_TOKENS.radius,
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 24px rgba(0,0,0,0.12)'
                    },
                    position: 'relative',
                    overflow: 'hidden',
                    height: 'auto',
                    minHeight: 280,
                    display: 'flex',
                    flexDirection: 'column',
                    border: '1px solid',
                    borderColor: 'divider'
                  }}
                  onClick={() => handleSubjectClick(subject)}
                >
                  {/* Progress overlay */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: 6,
                      backgroundColor: 'rgba(0,0,0,0.05)',
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        height: '100%',
                        width: `${Number(subject.progress || 0)}%`,
                        backgroundColor: subject.color,
                        transition: 'width 0.3s ease'
                      }
                    }}
                  />

                  <CardContent sx={{ p: 3, flex: 1, display: 'flex', flexDirection: 'column', textAlign: 'center' }}>
                    <Avatar
                      sx={{
                        bgcolor: subject.color,
                        width: 64,
                        height: 64,
                        mx: 'auto',
                        mb: 3
                      }}
                    >
                      <SchoolIcon sx={{ fontSize: 28 }} />
                    </Avatar>

                    <Typography 
                      variant="h6" 
                      fontWeight={700} 
                      mb={2} 
                      sx={{ 
                        minHeight: 32, 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        wordBreak: 'break-word'
                      }}
                    >
                      {subject.name}
                    </Typography>

                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, width: '100%' }}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          wordBreak: 'break-word',
                          whiteSpace: 'normal',
                          mb: 1
                        }}
                      >
                        {subject.description}
                      </Typography>
                      {subject.description && subject.description.length > 80 && (
                        <MuiLink
                          component="button"
                          variant="body2"
                          onClick={(e) => {
                            e.stopPropagation();
                            setReadMoreSubject(subject);
                          }}
                          sx={{ alignSelf: 'center', mb: 3, fontWeight: 600, color: 'primary.main' }}
                        >
                          Read More
                        </MuiLink>
                      )}
                    </Box>

                    <Box sx={{ mt: 'auto' }}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="body2" color="text.secondary">
                          Progress
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {subject.progress}%
                        </Typography>
                      </Box>

                      <LinearProgress
                        variant="determinate"
                        value={Number(subject.progress || 0)}
                        sx={{
                          height: 6,
                          borderRadius: UI_TOKENS.radius,
                          backgroundColor: 'rgba(0,0,0,0.05)',
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: subject.color
                          }
                        }}
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            ))}
          </Box>
        )}
        
        {/* Read More Dialog */}
        {readMoreSubject && (
          <Box
            sx={{
              position: 'fixed',
              top: 0, left: 0, right: 0, bottom: 0,
              bgcolor: 'rgba(0,0,0,0.5)',
              zIndex: 9999,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: 2
            }}
            onClick={(e) => { e.stopPropagation(); setReadMoreSubject(null); }}
          >
            <Card 
              sx={{ maxWidth: 500, width: '100%', borderRadius: 3, p: 2 }}
              onClick={(e) => e.stopPropagation()}
            >
              <CardContent>
                <Typography variant="h5" fontWeight={800} mb={2}>{readMoreSubject.name}</Typography>
                <Typography variant="body1" color="text.secondary" lineHeight={1.8}>
                  {readMoreSubject.description}
                </Typography>
                <Box textAlign="right" mt={3}>
                  <Button variant="contained" onClick={() => setReadMoreSubject(null)}>Close</Button>
                </Box>
              </CardContent>
            </Card>
          </Box>
        )}
      </Box>
    </Fade>
  );

  const renderSubjectDetail = () => (
    <Fade in={selectedView === 'subject'} timeout={500}>
      <Box>
        <Box 
          sx={{ 
            background: UI_TOKENS.heroGradient,
            color: 'text.primary',
            py: 6,
            borderBottom: '1px solid',
            borderColor: 'divider'
          }}
        >
          <Container maxWidth="xl">
            <Box display="flex" alignItems="center" mb={3}>
              <IconButton 
                onClick={() => setSelectedView('overview')}
                sx={{ mr: 2, color: UI_TOKENS.accent }}
              >
                <ArrowBackIcon />
              </IconButton>
              <Breadcrumbs 
                separator={<NavigateNextIcon fontSize="small" />}
                sx={{ color: 'text.secondary', fontWeight: 600 }}
              >
                <MuiLink 
                  component="button" 
                  onClick={() => setSelectedView('overview')}
                  sx={{ color: 'inherit', textDecoration: 'none', '&:hover': { color: UI_TOKENS.accent } }}
                >
                  Practice
                </MuiLink>
                <Typography fontWeight={700} sx={{ color: 'text.primary' }}>{selectedSubject?.name}</Typography>
              </Breadcrumbs>
            </Box>

            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={7}>
                <Box display="flex" alignItems="center" mb={2}>
                  <Avatar 
                    sx={{ 
                      bgcolor: UI_TOKENS.accent, 
                      width: 64, 
                      height: 64, 
                      mr: 3, 
                      borderRadius: UI_TOKENS.radius,
                      boxShadow: '0 8px 24px rgba(45, 91, 255, 0.2)'
                    }}
                  >
                    <SchoolIcon sx={{ fontSize: 32 }} />
                  </Avatar>
                  <Box>
                    <Typography variant="h3" fontWeight={900} sx={{ letterSpacing: '-0.02em', color: 'primary.dark' }}>
                      {selectedSubject?.name}
                    </Typography>
                    <Typography variant="h6" sx={{ opacity: 0.8, fontWeight: 500 }}>
                      {selectedSubject?.description}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} md={5}>
                <Card sx={{ bgcolor: 'white', borderRadius: UI_TOKENS.radius, boxShadow: '0 4px 20px rgba(0,0,0,0.05)', border: '1px solid', borderColor: 'divider' }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box display="flex" justifyContent="space-between" mb={1}>
                      <Typography variant="subtitle2" fontWeight={800} sx={{ textTransform: 'uppercase', color: 'text.secondary' }}>Course Mastery</Typography>
                      <Typography variant="subtitle2" fontWeight={800} color="primary.main">{selectedSubject?.progress}%</Typography>
                    </Box>
                    <LinearProgress 
                      variant="determinate" 
                      value={selectedSubject?.progress || 0} 
                      sx={{ 
                        height: 10, 
                        borderRadius: UI_TOKENS.radius,
                        bgcolor: 'rgba(0,0,0,0.05)',
                        '& .MuiLinearProgress-bar': {
                          bgcolor: UI_TOKENS.accent
                        }
                      }}
                    />
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* Subject Progress */}
        <Grid container spacing={4} mb={6}>
          <Grid item xs={12} md={8}>
            <Card sx={{ borderRadius: UI_TOKENS.radius, border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
              <CardContent sx={{ p: 4 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6" fontWeight={800} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Course Mastery
                  </Typography>
                  <Typography variant="h4" fontWeight={900} color="primary.main">
                    {stats.overallProgress}%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={selectedSubject?.progress || 0}
                  sx={{
                    height: 12,
                    borderRadius: UI_TOKENS.radius,
                    backgroundColor: 'rgba(0,0,0,0.05)',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: selectedSubject?.color
                    }
                  }}
                />
                <Box display="flex" justifyContent="space-between" mt={2}>
                  <Typography variant="body2" fontWeight={600} color="text.secondary">
                    {selectedSubject?.completedTopics} of {selectedSubject?.totalTopics} chapters completed
                  </Typography>
                  <Typography variant="body2" fontWeight={600} color="text.secondary">
                    Estimated {selectedSubject?.estimatedHours}h to complete
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Grid container spacing={3}>
              <Grid item xs={6}>
                  <Card sx={{ borderRadius: UI_TOKENS.radius, textAlign: 'center', height: '100%', border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
                  <CardContent sx={{ p: 3 }}>
                    <Typography variant="h4" fontWeight={900} color="primary" mb={0.5}>
                      {selectedSubject?.completedTopics}
                    </Typography>
                    <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase' }}>
                      Completed
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6}>
                  <Card sx={{ borderRadius: UI_TOKENS.radius, textAlign: 'center', height: '100%', border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
                  <CardContent sx={{ p: 3 }}>
                    <Typography variant="h4" fontWeight={900} mb={0.5}>
                      {selectedSubject?.totalTopics}
                    </Typography>
                    <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase' }}>
                      Chapters
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        {/* Topics Grid */}
        <Typography variant="h5" fontWeight={600} mb={3}>
          Topics
        </Typography>

        <Alert severity="info" sx={{ mb: 3, borderRadius: UI_TOKENS.radius }}>
          <Typography variant="body2">
            <strong>Current Focus:</strong> {selectedSubject?.currentTopic} •
            <strong> Next:</strong> {selectedSubject?.nextConcept}
          </Typography>
        </Alert>

        <TopicGrid
          topics={selectedSubject?.topics || []}
          onTopicClick={(topic) => handleTopicClick(selectedSubject, topic)}
          loading={false}
        />
      </Box>
    </Fade>
  );

  const renderTopicDetail = () => (
    <Fade in={selectedView === 'topic'} timeout={500}>
      <Box>
        {/* Breadcrumb Navigation */}
        <Box mb={4}>
          <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />}>
            <MuiLink
              component="button"
              variant="body2"
              onClick={handleBackToOverview}
              sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}
            >
              <HomeIcon sx={{ mr: 0.5, fontSize: 16 }} />
              Subjects
            </MuiLink>
            <MuiLink
              component="button"
              variant="body2"
              onClick={handleBackToSubject}
              sx={{ textDecoration: 'none' }}
            >
              {selectedSubject?.name}
            </MuiLink>
            <Typography variant="body2" color="text.primary">
              {selectedTopic?.name}
            </Typography>
          </Breadcrumbs>
        </Box>

        <Card sx={{ mb: 4, borderRadius: UI_TOKENS.radius, color: '#fff', background: UI_TOKENS.heroGradient, boxShadow: '0 8px 32px rgba(26,35,126,0.15)' }}>
          <CardContent sx={{ p: 4 }}>
            <Stack direction="row" spacing={3} alignItems="center">
              <IconButton onClick={handleBackToSubject} sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,0.1)', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }}>
                <ArrowBackIcon />
              </IconButton>
              <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 64, height: 64, borderRadius: UI_TOKENS.radius }}>
                <TimelineIcon sx={{ fontSize: 32 }} />
              </Avatar>
              <Box>
                <Typography variant="h4" fontWeight={900} sx={{ letterSpacing: '-0.01em' }}>{selectedTopic?.name}</Typography>
                <Typography variant="body1" sx={{ opacity: 0.9, mt: 0.5 }}>Inside {selectedSubject?.name}</Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        {/* Topic Progress */}
        <Card sx={{ borderRadius: UI_TOKENS.radius, mb: 6, border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
          <CardContent sx={{ p: 4 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6" fontWeight={800} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Topic Proficiency
              </Typography>
              <Typography variant="h4" fontWeight={900} color="primary">
                {selectedTopic ? Math.round((selectedTopic.subtopics?.filter(s => s.completed).length / selectedTopic.subtopics?.length) * 100) : 0}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={selectedTopic ? (selectedTopic.subtopics?.filter(s => s.completed).length / selectedTopic.subtopics?.length) * 100 : 0}
              sx={{
                height: 12,
                borderRadius: UI_TOKENS.radius,
                backgroundColor: 'rgba(0,0,0,0.05)',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: selectedSubject?.color
                }
              }}
            />
            <Box display="flex" justifyContent="space-between" mt={2}>
              <Typography variant="body2" fontWeight={600} color="text.secondary">
                {selectedTopic ? selectedTopic.subtopics?.filter(s => s.completed).length : 0} of {selectedTopic?.subtopics?.length || 0} concepts mastered
              </Typography>
              <Typography variant="body2" fontWeight={600} color="text.secondary">
                ~{selectedTopic ? Math.ceil((selectedTopic.subtopics?.filter(s => !s.completed).length || 0) * 0.5) : 0} study hours remaining
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {/* Subtopics and Concepts List */}
        <Typography variant="h4" fontWeight={600} mb={4} textAlign="center">
          Subtopics & Concepts
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {selectedTopic?.subtopics?.map((subtopic, stIndex) => (
            <Paper 
              key={subtopic.id} 
              elevation={0}
              sx={{ 
                p: 4, 
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: UI_TOKENS.radius,
                bgcolor: 'background.paper'
              }}
            >
              {/* Subtopic Header */}
              <Box mb={4}>
                <Box display="flex" alignItems="center" mb={2}>
                  <Avatar
                    sx={{
                      bgcolor: subtopic.completed ? '#4CAF50' : subtopic.inProgress ? UI_TOKENS.accent : '#E0E0E0',
                      width: 48,
                      height: 48,
                      mr: 2.5,
                      borderRadius: UI_TOKENS.radius
                    }}
                  >
                    {subtopic.completed ? <CompletedIcon sx={{ fontSize: 28 }} /> :
                     subtopic.inProgress ? <PlayIcon sx={{ fontSize: 28 }} /> :
                     <LockIcon sx={{ fontSize: 24 }} />}
                  </Avatar>
                  <Box flex={1}>
                    <Typography variant="h6" fontWeight={800} sx={{ letterSpacing: '-0.01em' }}>
                      {stIndex + 1}. {subtopic.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                      {subtopic.description}
                    </Typography>
                  </Box>
                  <Chip
                    label={`${subtopic.completedConcepts || 0}/${subtopic.totalConcepts || subtopic.concepts?.length || 0} Mastering`}
                    size="small"
                    color={subtopic.completed ? 'success' : 'default'}
                    variant="outlined"
                    sx={{ borderRadius: UI_TOKENS.radius, fontWeight: 700 }}
                  />
                </Box>
                
                {subtopic.progress > 0 && (
                  <LinearProgress
                    variant="determinate"
                    value={subtopic.progress || 0}
                    sx={{
                      height: 6,
                      borderRadius: UI_TOKENS.radius,
                      mt: 2,
                      backgroundColor: 'rgba(0,0,0,0.05)',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: UI_TOKENS.accent
                      }
                    }}
                  />
                )}
              </Box>

              {/* Concepts Grid */}
              <Grid container spacing={2}>
                {subtopic.concepts?.map((concept, cIndex) => (
                  <Grid item xs={12} sm={6} md={4} key={concept.id}>
                    <Card
                      sx={{
                        cursor: concept.locked ? 'not-allowed' : 'pointer',
                        opacity: concept.locked ? 0.6 : 1,
                        transition: 'all 0.3s ease',
                        '&:hover': concept.locked ? {} : {
                          transform: 'translateY(-4px)',
                          boxShadow: '0 8px 24px rgba(0,0,0,0.08)'
                        },
                        height: '100%',
                        borderRadius: UI_TOKENS.radius,
                        border: '1px solid',
                        borderColor: concept.inProgress ? UI_TOKENS.accent : 'divider',
                        borderWidth: concept.inProgress ? 2 : 1,
                        boxShadow: 'none'
                      }}
                      onClick={() => !concept.locked && navigate(`/subject-practice/${selectedSubject.id}/topic/${selectedTopic.id}/subtopic/${subtopic.id}/concept/${concept.id}`)}
                    >
                      <CardContent sx={{ p: 2.5 }}>
                        <Box display="flex" alignItems="flex-start" mb={2}>
                          <Avatar
                            sx={{
                              bgcolor: concept.completed ? '#4CAF50' : concept.inProgress ? UI_TOKENS.accent : '#E0E0E0',
                              width: 36,
                              height: 36,
                              mr: 2,
                              fontSize: 14,
                              borderRadius: UI_TOKENS.radius
                            }}
                          >
                            {concept.completed ? <CompletedIcon sx={{ fontSize: 18 }} /> :
                             concept.inProgress ? <PlayIcon sx={{ fontSize: 18 }} /> :
                             <LockIcon sx={{ fontSize: 16 }} />}
                          </Avatar>
                          <Box flex={1}>
                            <Typography variant="body1" fontWeight={600} sx={{ lineHeight: 1.3, mb: 0.5 }}>
                              {concept.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                              {concept.duration} min
                            </Typography>
                          </Box>
                        </Box>

                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', justifyContent: 'space-between' }}>
                          <Chip
                            label={concept.completed ? 'Mastered' : concept.inProgress ? 'Learning' : 'Locked'}
                            size="small"
                            color={concept.completed ? 'success' : concept.inProgress ? 'primary' : 'default'}
                            sx={{ 
                              height: 22, 
                              fontSize: '0.75rem',
                              fontWeight: 700,
                              borderRadius: UI_TOKENS.radius
                            }}
                          />

                          {!concept.completed && (
                            <Button
                              size="small"
                              variant="text"
                              sx={{ fontWeight: 700, borderRadius: UI_TOKENS.radius }}
                              onClick={(e) => { e.stopPropagation(); markConceptCompleted(concept); }}
                            >
                              Mark Done
                            </Button>
                          )}

                          {syncingItems[concept.id] && (
                            <CircularProgress size={18} thickness={6} />
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          ))}
        </Box>
      </Box>
    </Fade>
  );

  return (
    <ErrorBoundary>
      <Container maxWidth="xl" sx={{ py: 6 }}>
        {/* Offline banner removed - using API-first approach */}

        {/* Error Banner */}
        {error && (
          <Alert 
            severity="error" 
            onClose={() => setError('')}
            sx={{ mb: 4, borderRadius: UI_TOKENS.radius }}
          >
            <Typography variant="body2" fontWeight={600}>{error}</Typography>
            <Button
              variant="contained"
              size="small"
              sx={{ mt: 1.5, borderRadius: UI_TOKENS.radius }}
              onClick={loadSubjects}
            >
              Retry Connection
            </Button>
          </Alert>
        )}

        {selectedView === 'overview' && renderOverview()}
        {selectedView === 'subject' && renderSubjectDetail()}
        {selectedView === 'topic' && renderTopicDetail()}
      </Container>
    </ErrorBoundary>
  );
};

export default SubjectPractice;